import { ReactNode } from 'react';
import {
  ContractInsurance,
  NewContractInsurance,
} from '../../../../types/insurance';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { renderSkeletonCards } from '../../../../components/SkeletonElements';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import DeleteInsuranceConfirmationDialog from './DeleteInsuranceConfirmationDialog';
import InsuranceCard from '../../../../components/InsuranceCard';
import {
  ListElement,
  UseDraftListResult,
} from '../../../../hooks/useDraftList';
import { Contract } from '../../../../types/contract';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';
import AddButtonCard from '../../../../components/AddButtonCard';

type InsuranceTabContentProps = {
  contract: Contract;
  draftListManager: UseDraftListResult<ContractInsurance>;
  emptyContent: ReactNode;
  showAddButton?: boolean;
  areInsurancesPending: boolean;
  isFetchingInsurances: boolean;
  onInsurancesDataUpdate: () => void;
  canInteractWithInsurances?: boolean;
};

const InsuranceTabContent = ({
  areInsurancesPending,
  draftListManager,
  isFetchingInsurances,
  onInsurancesDataUpdate,
  contract,
  emptyContent,
  showAddButton = false,
  canInteractWithInsurances = false,
}: InsuranceTabContentProps) => {
  const { t } = useLanguage();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const { addContractInsurance, deleteContractInsurance } = useIotsApi();

  const [insuranceListElementToDelete, setInsuranceListElementToDelete] =
    useState<ListElement<ContractInsurance> | null>(null);

  const {
    mutate: applyAddInsuranceMutation,
    isPending: isAddInsurancePending,
    reset: resetAddInsuranceMutation,
  } = useMutation({
    mutationFn: (newInsurance: NewContractInsurance) =>
      addContractInsurance(contract.id, newInsurance),
  });

  const {
    mutate: applyDeleteInsuranceMutation,
    isPending: isDeleteInsurancePending,
    reset: resetDeleteInsuranceutation,
  } = useMutation({
    mutationFn: (insuranceId: string) =>
      deleteContractInsurance(contract.id, insuranceId),
  });

  if (areInsurancesPending) {
    return <div className="flex flex-col gap-4">{renderSkeletonCards(3)}</div>;
  }

  const { isReady, list, operations } = draftListManager;

  if (!list.length) {
    return (
      <>
        {emptyContent}
        {showAddButton && (
          <AddButtonCard
            disabled={!isReady}
            onClick={() => operations.addDraftElement()}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {showAddButton && (
        <AddButtonCard
          disabled={!isReady}
          onClick={() => operations.addDraftElement()}
        />
      )}
      {list.map(item =>
        item.isDraft ? (
          <InsuranceCard
            insuranceType="contract"
            insurance={null}
            isLoading={isFetchingInsurances || isAddInsurancePending}
            onDelete={hasPendingChanges => {
              if (hasPendingChanges) {
                setInsuranceListElementToDelete(item);
              } else {
                operations.removeElement(item);
              }
            }}
            onSave={newInsurance => {
              applyAddInsuranceMutation(newInsurance, {
                onSuccess: insurance => {
                  operations.promoteDraftToPersistentElement(
                    item.id,
                    insurance,
                  );
                  enqueueSnackbar({
                    variant: 'success',
                    message: t('content.insurance-creation-success'),
                  });
                  onInsurancesDataUpdate();
                },
                onError: error => {
                  enqueueSnackbar({
                    variant: 'error',
                    message: getErrorMessage(
                      error,
                      t('content.insurance-creation-error'),
                    ),
                  });
                  resetAddInsuranceMutation();
                },
              });
            }}
            key={item.id}
          />
        ) : (
          <InsuranceCard
            insuranceType="contract"
            insurance={item.data}
            isLoading={isFetchingInsurances || isAddInsurancePending}
            onSave={() => {}}
            onDelete={() => {
              setInsuranceListElementToDelete(item);
            }}
            key={item.data.id}
            areActionsDisabled={!canInteractWithInsurances}
          />
        ),
      )}
      <DeleteInsuranceConfirmationDialog
        insuranceItem={insuranceListElementToDelete}
        isLoading={isDeleteInsurancePending}
        onClose={() => {
          if (!isDeleteInsurancePending) {
            setInsuranceListElementToDelete(null);
          }
        }}
        onConfirm={() => {
          if (!insuranceListElementToDelete) {
            return;
          }
          if (insuranceListElementToDelete.isDraft) {
            operations.removeElement(insuranceListElementToDelete);
            setInsuranceListElementToDelete(null);
            return;
          }

          const { data: insurance } = insuranceListElementToDelete;
          applyDeleteInsuranceMutation(insurance.id, {
            onSuccess: () => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.insurance-deletion-success'),
              });
              operations.removeElement(insuranceListElementToDelete);
              setInsuranceListElementToDelete(null);
              onInsurancesDataUpdate();
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.insurance-deletion-error'),
                ),
              });
              resetDeleteInsuranceutation();
            },
          });
        }}
      />
    </div>
  );
};

export default InsuranceTabContent;
